/*
 * TAKEDA CONFIDENTIAL – Highly Restricted: Do not distribute without prior approval
 *
 * © Copyright (2026) Takeda. All Rights Reserved
 */

import './App.scss';

import React, { FC, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';

import {
  getAuthenticationError,
  getMsalConfig,
} from './domain/authentication/authentication.domain';
import { LoginType } from './domain/authentication/authentication.types';
import { getBaseUrl, isAppSelfHosted, isSafari } from './domain/browser.domain';
import {
  getCountryConfiguration,
  getCountryDatacenterUrl,
} from './domain/global-config.domain';
import { buildLocale } from './domain/i18n/i18n.domain';
import {
  setTrackedCustomUrl,
  setTrackedDocumentTitle,
} from './domain/tracking/tracking.domain';
import Authenticated from './modules/authentication/login/authenticated.component';
import { VdzDemoBanner } from './modules/banners/demo-banner/demo-banner.component';
import { VdzNotificationBanner } from './modules/banners/notification-banner/notification-banner.component';
import { COUNTRY_UNAVAILABLE } from './modules/error-modal/components/error.constants';
import { VdzHeader } from './modules/header/components/header.component';
import { VdzLocalePicker } from './modules/locale-picker/components/locale-picker.component';
import {
  CALCULATOR_PAGE,
  CONTACT_US_PAGE,
  COOKIE_POLICY_PAGE,
  DOC_CHECK_LOGIN_PAGE,
  LOGIN_PAGE,
  PRESCRIBING_INFORMATION_PAGE,
  RESOURCES_PAGE,
  SIGN_UP_PAGE,
  TAKEDA_ID_LOGIN_PAGE,
} from './modules/navigation/navigation.constants';
import { VdzSpinner } from './modules/shared/components/spinner.component';
import { useDocumentTitle } from './modules/shared/hooks/use-document-title/use-document-title.hook';
import { useEnvConfig } from './modules/shared/hooks/use-env-config/use-env-config.hook';
import { useGlobalConfig } from './modules/shared/hooks/use-global-config/use-global-config.hook';
import { useLocale } from './modules/shared/hooks/use-locale/use-locale.hook';
import { useParams } from './modules/shared/hooks/use-params/user-params.hook';
import { VdzTermsUpdatedConfirmation } from './modules/terms/components/terms-updated-confirmation/terms-updated-confirmation.component';
import { CalculatorPage } from './pages/calculator.page';
import { ContactUsAboutPage } from './pages/contact-us-about.page';
import { CookiePolicyPage } from './pages/cookie-policy.page';
import { LoginSelectionPage } from './pages/login-selection/login-selection.page';
import { LoginPage } from './pages/login.page';
import { PrescribingInformationPage } from './pages/prescribing-information.page';
import { ResourcesPage } from './pages/resources.page';
import { SignUpPage } from './pages/signup.page';
import { getEnvConfig } from './services/env-config/env-config.service';
import { getGlobalConfigData } from './services/global-config/global-config.service';
import { baseline } from './services/i18n/i18n.constants';
import { i18nService } from './services/i18n/i18n.service';

i18nService.init();

const App = () => {
  const navigate = useNavigate();
  const msalInstance = useRef<PublicClientApplication>();
  const { params, getStringParam, getBooleanParam } = useParams();
  const { pathname } = useLocation();
  const { globalConfig, setGlobalConfig } = useGlobalConfig();
  const { setEnvConfig } = useEnvConfig();
  const { country, language, locale, setLocale, clearLocale, context } =
    useLocale();
  const { i18n } = useTranslation();
  const [bypassLogin, setBypassLogin] = useState(false);
  const [msalReady, setMsalReady] = useState(false);

  const hasLocale = !!locale || !!context.locale;

  useDocumentTitle('header.title');

  useEffect(() => {
    if (isSafari()) {
      document.getElementsByTagName('html')[0].classList.add('safari');
    }

    processOneTrust();

    if (hasLocale) {
      document.documentElement.lang = language;
      window.OneTrust?.changeLanguage();
    }

    const loadConfigData = async () => {
      const globalConfig = await getGlobalConfigData();
      setGlobalConfig(globalConfig);

      const envConfig = await getEnvConfig();
      setEnvConfig(envConfig);

      msalInstance.current = new PublicClientApplication(
        getMsalConfig(envConfig)
      );
      await msalInstance.current.initialize();
      const redirectResponse = await msalInstance.current
        .handleRedirectPromise()
        .catch((err) => {
          console.error(err);
          return null;
        });

      if (redirectResponse) {
        msalInstance.current.setActiveAccount(redirectResponse.account);

        const authErrorType = getAuthenticationError(
          redirectResponse.idTokenClaims,
          globalConfig.countryConfiguration,
          country
        );

        setMsalReady(true);

        if (authErrorType) {
          msalInstance.current.setActiveAccount(null);
          navigate(SIGN_UP_PAGE, {
            replace: true,
            state: { authErrorType },
          });
        } else {
          navigate(CALCULATOR_PAGE, { replace: true });
        }

        return;
      }

      setMsalReady(true);

      const hasParams = !!params.toString();
      if (hasParams) {
        const paramCountry = getStringParam('country')?.toUpperCase();
        const paramLanguage = getStringParam('language')?.toLowerCase();
        const skipSelection = getBooleanParam('skipSelection');
        const isHCP = getBooleanParam('isHCP');
        const loggedIn = getBooleanParam('loggedIn');

        const countryConfig = getCountryConfiguration(
          paramCountry,
          globalConfig.countryConfiguration
        );

        const selectedLocale = buildLocale(paramLanguage, paramCountry);
        if (selectedLocale) {
          i18nService.changeLocale(selectedLocale);
          if (skipSelection) {
            setLocale(selectedLocale);
          }
        }

        if (loggedIn && countryConfig?.canBypassLogin) {
          setBypassLogin(loggedIn);
        }

        if (skipSelection && loggedIn) {
          navigate(CALCULATOR_PAGE);
        } else {
          navigate(LOGIN_PAGE, {
            state: {
              country: paramCountry,
              language: paramLanguage,
              isHCP,
              loggedIn,
              skipSelection,
            },
          });
        }
      } else if (!hasLocale && pathname !== LOGIN_PAGE) {
        navigate(LOGIN_PAGE);
      }
    };

    loadConfigData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on load only; should not re-run on navigation
  }, []);

  useEffect(() => {
    const baseUrl = getBaseUrl();
    const datacenterUrl = country
      ? getCountryDatacenterUrl(country, globalConfig.countryConfiguration)
      : undefined;
    if (datacenterUrl && !baseUrl.startsWith(datacenterUrl)) {
      const pathname = window.location.pathname;
      const redirectUrl = `${datacenterUrl}${pathname}${window.location.search}`;
      console.log(`Redirecting to :${redirectUrl}`);
      window.location.replace(redirectUrl);
    }

    if (country && globalConfig.countryConfiguration) {
      const countryConfig = getCountryConfiguration(
        country,
        globalConfig.countryConfiguration
      );
      if (!countryConfig) {
        i18nService.changeLocale(baseline);
        clearLocale();
        navigate(LOGIN_PAGE, { state: { error: COUNTRY_UNAVAILABLE } });
      }
    }
  }, [country, globalConfig, clearLocale, navigate]);

  useEffect(() => {
    if (locale && i18n.language !== locale) {
      i18nService.changeLocale(locale);
    }
  }, [locale, i18n.language]);

  useEffect(() => {
    setTrackedCustomUrl();
    setTrackedDocumentTitle();
  }, [pathname]);

  const processOneTrust = () => {
    let oneTrustInterval: NodeJS.Timer | undefined = undefined;

    const hookOneTrust = () => {
      const oneTrustWrapper = document.querySelector('#onetrust-consent-sdk');
      if (oneTrustWrapper) {
        // show OneTrust when the site is self hosted (as opposed to running withing an iframe)
        if (isAppSelfHosted()) oneTrustWrapper.classList.add('visible');

        clearInterval(oneTrustInterval);
      }
    };

    // start an interval as fallback, but run immediately already
    oneTrustInterval = setInterval(hookOneTrust, 250);
    hookOneTrust();
  };

  const AuthenticatedComponent: FC<{ success: JSX.Element }> = ({
    success,
  }) => (
    <>
      {locale && <VdzTermsUpdatedConfirmation />}

      {bypassLogin ? (
        success
      ) : (
        <Authenticated success={success} loading={<VdzSpinner />} />
      )}
    </>
  );

  return globalConfig && msalReady && msalInstance.current ? (
    <MsalProvider instance={msalInstance.current}>
      <div dir={i18n.dir()} className="app relative print:min-h-[100%]">
        <Suspense fallback={<VdzSpinner />}>
          {!hasLocale && <VdzLocalePicker />}
          <VdzNotificationBanner />
          <VdzDemoBanner />
          <VdzHeader />
          <Routes>
            <Route
              path={CALCULATOR_PAGE}
              element={<AuthenticatedComponent success={<CalculatorPage />} />}
            />
            <Route
              path={RESOURCES_PAGE}
              element={<AuthenticatedComponent success={<ResourcesPage />} />}
            />
            <Route
              path={CONTACT_US_PAGE}
              element={
                <AuthenticatedComponent success={<ContactUsAboutPage />} />
              }
            />
            <Route
              path={COOKIE_POLICY_PAGE}
              element={
                <AuthenticatedComponent success={<CookiePolicyPage />} />
              }
            />
            <Route
              path={PRESCRIBING_INFORMATION_PAGE}
              element={
                <AuthenticatedComponent
                  success={<PrescribingInformationPage />}
                />
              }
            />
            <Route path={SIGN_UP_PAGE} element={<SignUpPage />} />
            <Route path={LOGIN_PAGE} element={<LoginSelectionPage />} />
            <Route
              path={TAKEDA_ID_LOGIN_PAGE}
              element={<LoginPage type={LoginType.TakedaId} />}
            />
            <Route
              path={DOC_CHECK_LOGIN_PAGE}
              element={<LoginPage type={LoginType.DocCheck} />}
            />
          </Routes>
        </Suspense>
      </div>
    </MsalProvider>
  ) : (
    <Suspense />
  );
};

export default App;
